import React, { useMemo } from 'react';
import { PreloadProgress, PreloadStage } from '@/core/offline/useOfflinePreloader';

interface OfflineProgressBarProps {
  progress: PreloadProgress;
  isPreloading: boolean;
  isSyncing?: boolean;
  isOnline?: boolean;
  className?: string;
}

// Stage weights for progress calculation
const STAGE_WEIGHTS: Record<PreloadStage, { start: number; end: number }> = {
  idle: { start: 0, end: 0 },
  ra_profiles: { start: 0, end: 30 },
  shop_products: { start: 30, end: 45 },
  shop_deliveries: { start: 45, end: 60 },
  shop_returns: { start: 60, end: 75 },
  laundry_movements: { start: 75, end: 95 },
  complete: { start: 100, end: 100 },
  error: { start: 0, end: 0 },
};

// Stage labels for aria
const STAGE_LABELS: Record<PreloadStage, string> = {
  idle: 'In attesa',
  ra_profiles: 'Caricamento profili RA',
  shop_products: 'Caricamento prodotti shop',
  shop_deliveries: 'Caricamento storico consegne',
  shop_returns: 'Caricamento storico resi',
  laundry_movements: 'Caricamento movimenti lavanderia',
  complete: 'Completato',
  error: 'Errore',
};

export const OfflineProgressBar: React.FC<OfflineProgressBarProps> = ({
  progress,
  isPreloading,
  isSyncing = false,
  isOnline = true,
  className = '',
}) => {
  // Calculate overall progress percentage
  const overallProgress = useMemo(() => {
    if (progress.stage === 'idle' || progress.stage === 'error') return 0;
    if (progress.stage === 'complete') return 100;

    const stageWeight = STAGE_WEIGHTS[progress.stage];
    const stageRange = stageWeight.end - stageWeight.start;

    // Calculate progress within current stage
    const stageProgress = progress.total > 0
      ? (progress.current / progress.total)
      : 0;

    return stageWeight.start + (stageRange * stageProgress);
  }, [progress.stage, progress.current, progress.total]);

  // Offline state - show red bar with message
  if (!isOnline) {
    return (
      <div
        className={`fixed top-0 left-0 right-0 z-[9999] bg-red-500 text-white text-center py-1 text-sm font-medium shadow-md ${className}`}
        role="alert"
        aria-label="Sei offline"
      >
        <span className="inline-flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728m-3.536-3.536a4 4 0 010-5.656m-7.072 7.072a9 9 0 010-12.728m3.536 3.536a4 4 0 010 5.656" />
          </svg>
          Sei offline - I dati salvati sono disponibili
        </span>
      </div>
    );
  }

  // Don't render if not active
  if (!isPreloading && !isSyncing && progress.stage !== 'complete') {
    return null;
  }

  // Hide after completion (with small delay for animation)
  if (progress.stage === 'complete' && !isSyncing) {
    return (
      <div
        className={`fixed top-0 left-0 right-0 z-[9999] h-[5px] bg-green-500 transition-all duration-500 ease-out ${className}`}
        style={{ width: '100%' }}
        role="progressbar"
        aria-valuenow={100}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Pre-caricamento completato"
      />
    );
  }

  // Error state
  if (progress.stage === 'error') {
    return (
      <div
        className={`fixed top-0 left-0 right-0 z-[9999] h-[5px] bg-red-500 ${className}`}
        role="alert"
        aria-label={`Errore: ${progress.error || 'Pre-caricamento fallito'}`}
      />
    );
  }

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-[9999] h-[5px] bg-gray-200/50 overflow-hidden ${className}`}
      role="progressbar"
      aria-valuenow={Math.round(overallProgress)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={STAGE_LABELS[progress.stage]}
    >
      {/* Determinate progress bar */}
      {isPreloading && !isSyncing && (
        <div
          className="h-full bg-primary transition-all duration-300 ease-out"
          style={{ width: `${overallProgress}%` }}
        />
      )}

      {/* Indeterminate animation for sync */}
      {isSyncing && (
        <div className="h-full w-full relative">
          <div className="absolute inset-0 bg-blue-500 animate-indeterminate-progress" />
        </div>
      )}
    </div>
  );
};

export default OfflineProgressBar;
